// knowledgeBankModel — the Knowledge bank's pure half (#60, #89): what kind
// of file a source is, which viewer shows it, and the list as shown.
//
//   fileKindOf     a filename (+ the stored media type, when known) → the
//                  file kind the row and the viewer name.
//   viewerOf       a source → the ONE viewer that shows it. Every kind the app
//                  cannot show as it is (Word, presentations, anything
//                  unknown, a lost original) falls to `default` (E-13).
//   knowledgeRowsOf / shownRows   the list: sources and raw files, searched,
//                  sorted, archived apart.
//   middleTruncate a long file name keeps its start AND its extension.
//
// Pure: values in, values out. No React, no copy (the view localises).

import type { Library } from "../../app/ports/workspace.js";
import type { SourceReadStatus } from "../../app/sourceReadStatus.js";
import type { SearchResult } from "../Search/searchModel.js";
import type { SourceProjection } from "../SourceView/types.js";

// === File kinds ==============================================================

export type FileKind =
    | "pdf"
    | "word"
    | "presentation"
    | "text"
    | "image"
    | "audio"
    | "video"
    | "unknown";

const EXTENSION_KINDS: ReadonlyMap<string, FileKind> = new Map([
    ["pdf", "pdf"],
    ["docx", "word"],
    ["doc", "word"],
    ["pptx", "presentation"],
    ["ppt", "presentation"],
    ["txt", "text"],
    ["md", "text"],
    ["png", "image"],
    ["jpg", "image"],
    ["jpeg", "image"],
    ["gif", "image"],
    ["webp", "image"],
    ["mp3", "audio"],
    ["wav", "audio"],
    ["m4a", "audio"],
    ["mp4", "video"],
    ["webm", "video"],
    ["mov", "video"],
]);

const kindOfMediaType = (mediaType: string): FileKind | undefined => {
    if (mediaType === "application/pdf") return "pdf";
    if (
        mediaType.includes("wordprocessingml") ||
        mediaType === "application/msword"
    )
        return "word";
    if (
        mediaType.includes("presentationml") ||
        mediaType.includes("powerpoint")
    )
        return "presentation";
    if (mediaType === "text/plain" || mediaType === "text/markdown")
        return "text";
    if (mediaType.startsWith("image/")) return "image";
    if (mediaType.startsWith("audio/")) return "audio";
    if (mediaType.startsWith("video/")) return "video";
    return undefined;
};

// The stored media type wins (it is what the bytes are); else the name's
// extension; else unknown.
export const fileKindOf = (filename: string, mediaType?: string): FileKind => {
    const byType =
        mediaType === undefined ? undefined : kindOfMediaType(mediaType);
    if (byType !== undefined) return byType;
    const dot = filename.lastIndexOf(".");
    const ext = dot < 0 ? "" : filename.slice(dot + 1).toLowerCase();
    return EXTENSION_KINDS.get(ext) ?? "unknown";
};

// === Viewers =================================================================

export type ViewerKind =
    | "pdf"
    | "word"
    | "image"
    | "audio"
    | "video"
    | "text"
    | "default";

// Which viewer shows a source. A viewer renders the ORIGINAL, never re-set
// text: the original's bytes must be there (`content` is upgraded to pdf /
// image / media only when they are). Text IS the original for a text file and
// for a source pasted as text (no stored file, no other kind in its name).
// Everything else, the default (E-13): no preview, the text that was read.
export const viewerOf = (source: {
    readonly fileKind: FileKind;
    readonly content: SourceProjection;
}): ViewerKind => {
    const { fileKind, content } = source;
    switch (content.type) {
        case "pdf":
            return "pdf";
        case "word":
            return "word";
        case "image":
            return "image";
        case "media":
            return content.kind;
        case "video":
            return "video";
        case "text":
            return fileKind === "text" || fileKind === "unknown"
                ? "text"
                : "default";
        case "unavailable":
            return "default";
    }
};

// === The list ================================================================

type KnowledgeRowKind = "source" | "file";

export type KnowledgeRow = {
    readonly id: string;
    readonly kind: KnowledgeRowKind;
    // The title a reader knows it by, and the stored name (usually the
    // uploaded file's name) its kind is read from.
    readonly title: string;
    readonly filename: string;
    readonly fileKind: FileKind;
    readonly createdAt?: string;
    readonly readStatus?: SourceReadStatus;
    readonly facts: number;
    readonly usedIn: number;
    readonly archived: boolean;
};

// The Knowledge bank's rows: every source and raw file the Library read
// holds, live and archived, titled as the rest of the app titles them.
export const knowledgeRowsOf = (
    results: readonly SearchResult[],
    library: Library | null,
): readonly KnowledgeRow[] => {
    if (library === null) return [];
    const items = new Map(
        [
            ...library.sources,
            ...library.files,
            ...library.archived.filter(
                (i) => i.type === "source" || i.type === "file",
            ),
        ].map((i) => [i.id, i]),
    );
    return results.flatMap((r): KnowledgeRow[] => {
        if (r.kind !== "source" && r.kind !== "file") return [];
        const item = items.get(r.id);
        const filename = item?.title ?? r.title;
        return [
            {
                id: r.id,
                kind: r.kind,
                title: r.title,
                filename,
                fileKind: fileKindOf(filename),
                ...(r.createdAt === undefined
                    ? {}
                    : { createdAt: r.createdAt }),
                ...(r.readStatus === undefined
                    ? {}
                    : { readStatus: r.readStatus }),
                facts: item?.type === "source" ? (item.facts ?? 0) : 0,
                usedIn: r.usedIn ?? 0,
                archived: r.archived === true,
            },
        ];
    });
};

export type KnowledgeSort = "added" | "title";

const loose = (s: string): string => s.toLocaleLowerCase().normalize("NFC");

// The rows shown: the archived ones or the live ones, those whose title or
// file name holds every word of the query, newest first or by title.
export const shownRows = (
    rows: readonly KnowledgeRow[],
    view: {
        readonly query: string;
        readonly sort: KnowledgeSort;
        readonly archived: boolean;
    },
): readonly KnowledgeRow[] => {
    const words = loose(view.query)
        .split(/\s+/)
        .filter((w) => w !== "");
    const matches = (row: KnowledgeRow): boolean => {
        const hay = loose(`${row.title} ${row.filename}`);
        return words.every((w) => hay.includes(w));
    };
    const compare = (a: KnowledgeRow, b: KnowledgeRow): number =>
        view.sort === "title"
            ? a.title.localeCompare(b.title)
            : (b.createdAt ?? "").localeCompare(a.createdAt ?? "") ||
              a.title.localeCompare(b.title);
    return rows
        .filter((row) => row.archived === view.archived && matches(row))
        .sort(compare);
};

// === Long names ==============================================================

// A long name is cut in the MIDDLE so its start and its end (the extension, a
// chapter number) both stay readable: "Kunskapsarbete och kunsk…kapitel 4.pdf".
// The view shrinks `head` with an ellipsis and keeps `tail` whole, so the cut
// follows the space there is. A short name is all head.
export const splitForMiddle = (
    text: string,
    tail = 14,
): { readonly head: string; readonly tail: string } => {
    const chars = [...text];
    return chars.length <= tail * 2
        ? { head: text, tail: "" }
        : {
              head: chars.slice(0, chars.length - tail).join(""),
              tail: chars.slice(chars.length - tail).join(""),
          };
};
